import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Card, FullColumn, Row, useBottomSheetContainer } from 'seagull';
import { ResponsiveContext, useResponsive } from '~/client-flow/contexts';
import { useStore } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { selectors } from '~/store/state-selectors';
import BrandingControllers from '../../BrandingControllers/BrandingControllers';
import { OverrideBranding } from '../../CobBrandingContext';
import { useCobConfig } from '../../CobConfigContext';
import { PreviewBanner, PreviewBannerNew } from '../../PreviewBanner';
import styles from './ClientOnboardingContent.module.scss';
import { ClientOnboardingContentDesktop } from './ClientOnboardingContentDesktop';
import { BrandRenderedOutsideProvider, ClientOnboardingContentMobile } from './ClientOnboardingContentMobile';
/** Overrides responsive to force mobile view. Used only for device-toggle preview. */ function PreviewMobileOverride(param) {
    let { children } = param;
    const context = useContext(ResponsiveContext);
    const value = useMemo(()=>_object_spread_props(_object_spread({}, context), {
            isMobile: true,
            isDesktop: false
        }), [
        context
    ]);
    return /*#__PURE__*/ _jsx(ResponsiveContext.Provider, {
        value: value,
        children: children
    });
}
export const ClientOnboardingContent = (props)=>{
    const { isMobile } = useResponsive();
    const { setContainer } = useBottomSheetContainer();
    const [mobileFrameEl, setMobileFrameEl] = useState(null);
    const mobileFrameRef = useCallback((el)=>setMobileFrameEl(el), []);
    const { previewState, localPreviewState, dismissPreviewModal, onToggleBrandSetup, showBrandSetupButton, deviceView, onDeviceViewChange } = props;
    const isPreview = !!previewState;
    const { CLIENT_PREVIEW_DEVICE_TOGGLE = false } = useFeatureFlags();
    const showMobile = CLIENT_PREVIEW_DEVICE_TOGGLE && onDeviceViewChange ? deviceView === 'mobile' : isMobile;
    const isDeviceToggleMobilePreview = CLIENT_PREVIEW_DEVICE_TOGGLE && !!onDeviceViewChange && deviceView === 'mobile';
    const isDeviceToggleDesktopPreview = CLIENT_PREVIEW_DEVICE_TOGGLE && !!onDeviceViewChange && deviceView === 'desktop';
    const { isSetupBrand } = useCobConfig();
    const worksAt = useStore(selectors.user.business);
    const content = showMobile ? /*#__PURE__*/ _jsx(ClientOnboardingContentMobile, _object_spread({}, props)) : /*#__PURE__*/ _jsx(ClientOnboardingContentDesktop, _object_spread({}, props));
    const deviceViewTransition = {
        initial: {
            opacity: 0,
            scale: 0.9
        },
        animate: {
            opacity: 1,
            scale: 1
        },
        exit: {
            opacity: 0,
            scale: 0.9
        },
        transition: {
            duration: 0.4,
            ease: 'easeInOut'
        }
    };
    useEffect(()=>{
        if (isDeviceToggleMobilePreview && mobileFrameEl) {
            setContainer(mobileFrameEl);
        }
        return ()=>{
            if (isDeviceToggleMobilePreview) {
                setContainer(null);
            }
        };
    }, [
        isDeviceToggleMobilePreview,
        mobileFrameEl,
        setContainer
    ]);
    const wrappedContent = CLIENT_PREVIEW_DEVICE_TOGGLE ? /*#__PURE__*/ _jsx(Card, {
        className: "".concat(styles.card, " ").concat(isDeviceToggleMobilePreview || isDeviceToggleDesktopPreview ? styles.cardWithPreview : ''),
        children: /*#__PURE__*/ _jsx(AnimatePresence, {
            mode: "wait",
            children: isDeviceToggleMobilePreview ? /*#__PURE__*/ _jsxs(Row, {
                className: styles.mobilePreviewWithBrand,
                gap: 0,
                children: [
                    /*#__PURE__*/ _jsx(motion.div, _object_spread_props(_object_spread({
                        className: styles.mobilePreviewSlot
                    }, deviceViewTransition), {
                        children: /*#__PURE__*/ _jsx("div", {
                            ref: mobileFrameRef,
                            className: styles.mobilePreviewFrame,
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.mobilePreviewInner,
                                children: /*#__PURE__*/ _jsx(BrandRenderedOutsideProvider, {
                                    value: true,
                                    children: /*#__PURE__*/ _jsx(PreviewMobileOverride, {
                                        children: content
                                    })
                                })
                            })
                        })
                    }), "mobile"),
                    isSetupBrand && worksAt && /*#__PURE__*/ _jsx(OverrideBranding, {
                        className: styles.setupBrandOutsideMobile,
                        testId: "setup-brand-section",
                        children: /*#__PURE__*/ _jsx(BrandingControllers, {
                            worksAt: worksAt
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: styles.desktopPreviewWithBrand,
                children: [
                    /*#__PURE__*/ _jsx(motion.div, _object_spread_props(_object_spread({
                        className: styles.desktopPreviewSlot
                    }, deviceViewTransition), {
                        children: /*#__PURE__*/ _jsx(BrandRenderedOutsideProvider, {
                            value: true,
                            children: content
                        })
                    }), "desktop"),
                    isSetupBrand && worksAt && /*#__PURE__*/ _jsx(OverrideBranding, {
                        className: styles.setupBrandOutsideDesktop,
                        testId: "setup-brand-section",
                        children: /*#__PURE__*/ _jsx(BrandingControllers, {
                            worksAt: worksAt
                        })
                    })
                ]
            })
        })
    }) : content;
    return /*#__PURE__*/ _jsxs(FullColumn, {
        className: styles.container,
        children: [
            isPreview && (CLIENT_PREVIEW_DEVICE_TOGGLE ? /*#__PURE__*/ _jsx(PreviewBannerNew, {
                previewState: previewState,
                localPreviewState: localPreviewState,
                onToggleBrandSetup: onToggleBrandSetup,
                onDismiss: dismissPreviewModal,
                showBrandSetupButton: showBrandSetupButton,
                deviceView: deviceView,
                onDeviceViewChange: onDeviceViewChange
            }) : /*#__PURE__*/ _jsx(PreviewBanner, {
                previewState: previewState,
                localPreviewState: localPreviewState,
                onToggleBrandSetup: onToggleBrandSetup,
                onDismiss: dismissPreviewModal,
                showBrandSetupButton: showBrandSetupButton
            })),
            wrappedContent
        ]
    });
};
